import React, { useEffect, useState, useCallback, useRef } from "react";
import styles from "./RecordingsTable.module.css";
import { useLocation, useNavigate } from "react-router-dom";
import { BASE_URL_DYNAMIC } from "../../../services/api/base-query";
import { getCookie } from "../../../utils/token";
import BackArrowButton from "../../backArrowButton/backArrowButton";
import {
    TMeetingRecord,
    MeetingRecordStatus,
    ProcessingStages,
    ProcessingStageSteps,
    TProcessingStage,
    mapMeetingRecordFromApi,
} from "../../../services/types/user.types";
import {
    useDeleteMeetingRecordsMutation,
    useGetUserMeetingRecordsMutation,
    useRetryMeetingRecordMutation,
    useLazyGetPendingRecordingSessionsQuery,
    useUpdateMeetingRecordAppointmentLinkMutation,
} from "../../../services/api/user.api";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import i18n from "../../../i18n";
import { useAppSelector } from "../../../services/hooks";
import { therapistIdSelector } from "../../../services/selectors/user.selectors";
import Modal from "../../modals/modal/Modal";
import PendingRecordingPopup from "../../modals/pendingRecordingPopup/pendingRecordingPopup";
import AppointmentPicker from "../../appointmentPicker/appointmentPicker";

// Format date to dd/mm/yyyy (converts UTC to local)
function formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return "";
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    } catch {
        return dateStr;
    }
}

// Format time to HH:mm (converts UTC to local)
// For new records: date field contains full UTC timestamp → convert to local
// For legacy records: date is midnight UTC → combine with utcTime (HH:mm) fallback → convert to local
function formatTime(dateStr: string | null | undefined, utcTime?: string | null): string {
    if (!dateStr) return "";
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return "";
        const isMidnight = date.getUTCHours() === 0 && date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0;
        if (!isMidnight) {
            // Full UTC timestamp — convert to local
            const hours = String(date.getHours()).padStart(2, "0");
            const mins = String(date.getMinutes()).padStart(2, "0");
            return `${hours}:${mins}`;
        }
        // Midnight UTC — try combining date portion with the separate UTC time string
        if (utcTime) {
            const datePart = dateStr.split("T")[0];
            const combined = new Date(`${datePart}T${utcTime}:00Z`);
            if (!isNaN(combined.getTime())) {
                const hours = String(combined.getHours()).padStart(2, "0");
                const mins = String(combined.getMinutes()).padStart(2, "0");
                return `${hours}:${mins}`;
            }
        }
        return "";
    } catch {
        return "";
    }
}

// Format phone to ddd-ddddddd (e.g., 050-1234567)
function formatPhone(phone: string | null | undefined): string {
    if (!phone) return "";
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, "");
    if (digits.length >= 10) {
        // Israeli format: 3 digits - 7 digits
        return `${digits.slice(0, 3)}-${digits.slice(3, 10)}`;
    } else if (digits.length >= 7) {
        // Shorter format: 3 digits - rest
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    return phone;
}

// Format duration in seconds to mm:ss or hh:mm:ss
function formatDuration(seconds: number | null | undefined): string {
    if (seconds === null || seconds === undefined) return "";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
        return `${hrs}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

function isFromToday(dateStr: string | null | undefined): boolean {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    const now = new Date();
    return (
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate()
    );
}

// Get the current step number for a processing stage
function getCurrentStep(stage: TProcessingStage | null | undefined): number {
    if (!stage) return 0;
    return ProcessingStageSteps[stage] || 0;
}

// Get the translated label for a processing stage
function getStageLabel(stage: TProcessingStage | null | undefined): string {
    if (!stage) return "";
    const stageLabels: Record<string, string> = {
        [ProcessingStages.UPLOADING]: t("STAGE_UPLOADING"),
        [ProcessingStages.TRANSCRIBING]: t("STAGE_TRANSCRIBING"),
        [ProcessingStages.ANALYZING]: t("STAGE_ANALYZING"),
        [ProcessingStages.FINALIZING]: t("STAGE_FINALIZING"),
        // Legacy stages
        [ProcessingStages.TEXT_ANALYSIS]: t("STAGE_ANALYZING"),
        [ProcessingStages.AUDIO_ANALYSIS]: t("STAGE_ANALYZING"),
        [ProcessingStages.SUMMARIZATION]: t("STAGE_ANALYZING"),
    };
    return stageLabels[stage] || stage;
}

// Get the status badge CSS class
function getStatusBadgeClass(status: string | undefined): string {
    switch (status) {
        case MeetingRecordStatus.COMPLETE:
            return styles.statusCompleted;
        case MeetingRecordStatus.DRAFT:
            return styles.statusDraft;
        case MeetingRecordStatus.PROCESSING:
            return styles.statusProcessing;
        case MeetingRecordStatus.FAILED:
            return styles.statusFailed;
        default:
            return styles.statusCompleted; // Default for old records without status
    }
}

// Get the status display text
function getStatusText(status: string | undefined): string {
    switch (status) {
        case MeetingRecordStatus.COMPLETE:
            return t("STATUS_COMPLETE");
        case MeetingRecordStatus.DRAFT:
            return t("STATUS_DRAFT");
        case MeetingRecordStatus.PROCESSING:
            return t("STATUS_PROCESSING");
        case MeetingRecordStatus.FAILED:
            return t("STATUS_FAILED");
        default:
            return t("STATUS_COMPLETE"); // Default for old records without status
    }
}

// Session storage key for persisting user details across page refresh
const RECORDINGS_TABLE_USER_KEY = "recordingsTableUserDetails";

// Helper to get user details from sessionStorage
function getStoredUserDetails(): Record<string, any> | null {
    try {
        const stored = sessionStorage.getItem(RECORDINGS_TABLE_USER_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch {
        return null;
    }
}

// Helper to save user details to sessionStorage
function saveUserDetails(details: Record<string, any>): void {
    try {
        sessionStorage.setItem(RECORDINGS_TABLE_USER_KEY, JSON.stringify(details));
    } catch {
        // Ignore storage errors
    }
}

export default function RecordingsTable() {
    const [filter, setFilter] = useState("");
    const [activeFilter, setActiveFilter] = useState("all");
    const navigate = useNavigate();
    const location = useLocation();
    const therapistId = useAppSelector(therapistIdSelector);
    const [triggerGetPendingSessions] = useLazyGetPendingRecordingSessionsQuery();
    const [pendingSessions, setPendingSessions] = useState<any[]>([]);
    const [showPendingModal, setShowPendingModal] = useState(false);
    const [showAppointmentPicker, setShowAppointmentPicker] = useState(false);
    const [recordForAppointmentLink, setRecordForAppointmentLink] = useState<TMeetingRecord | null>(null);
    const [updatingLinkRecordNumber, setUpdatingLinkRecordNumber] = useState<number | null>(null);
    const [checkingPending, setCheckingPending] = useState(false);
    
    // Support both navigation patterns:
    // 1. From meetingRecording: { Guid, UsersId, patientName, ... } directly in state
    // 2. From other components: { UserDetails: { Guid, UsersId, ... } } nested
    // 3. From sessionStorage after page refresh (fallback)
    const storedDetails = getStoredUserDetails();
    const userDetails = location.state?.UserDetails || {};
    
    // Prefer location.state, fall back to sessionStorage
    const userId = userDetails.UsersId || location.state?.UsersId || storedDetails?.UsersId;
    const Guid = userDetails.Guid || location.state?.Guid || storedDetails?.Guid;
    
    // Get the new record number if we just came from recording
    const newRecordNumber = location.state?.newRecordNumber;
    const userName = userDetails.UserName || location.state?.patientName || storedDetails?.patientName || "-";
    const userPhone = (userDetails.Phone || storedDetails?.patientPhone) ? formatPhone(userDetails.Phone || storedDetails?.patientPhone) : "-";
    const userBirth = (userDetails.Birth || storedDetails?.patientBirth) ? formatDate(userDetails.Birth || storedDetails?.patientBirth) : "-";
    const userPassport = userDetails.Passport || storedDetails?.patientPassport || "";
    const userEmail = userDetails.Email || location.state?.patientEmail || storedDetails?.patientEmail || "-";
    const userStatusId = userDetails.StatusId || location.state?.StatusId || storedDetails?.StatusId || 1;

    // Guard: if no patient context is available (e.g., after company switch
    // cleared sessionStorage and location.state), redirect to patients list.
    useEffect(() => {
        if (!Guid && !userId) {
            navigate("/users", { replace: true });
        }
    }, [Guid, userId, navigate]);

    // Persist user details to sessionStorage when we have valid data from navigation
    useEffect(() => {
        const hasLocationState = location.state?.UserDetails || location.state?.UsersId || location.state?.Guid;
        if (hasLocationState && Guid && userId) {
            saveUserDetails({
                Guid,
                UsersId: userId,
                patientName: userName,
                patientPhone: userPhone,
                patientBirth: userBirth,
                patientPassport: userPassport,
                patientEmail: userEmail,
                StatusId: userStatusId,
            });
        }
    }, [location.state, Guid, userId, userName, userPhone, userBirth, userPassport, userEmail, userStatusId]);

    const [allRecords] = useGetUserMeetingRecordsMutation();
    const [deleteRecord] = useDeleteMeetingRecordsMutation();
    const [retryRecord] = useRetryMeetingRecordMutation();
    const [updateAppointmentLink] = useUpdateMeetingRecordAppointmentLinkMutation();
    const [data, setData] = useState<TMeetingRecord[] | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [retryingRecords, setRetryingRecords] = useState<Set<number>>(new Set());

    // Audio playback state
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const blobUrlRef = useRef<string | null>(null);
    const [playingRecordNumber, setPlayingRecordNumber] = useState<number | null>(null);
    const [loadingAudioRecord, setLoadingAudioRecord] = useState<number | null>(null);

    const toggleAudioPlayback = useCallback(async (recordNumber: number, audioUrl: string) => {
        if (playingRecordNumber === recordNumber && audioRef.current) {
            // Currently playing this record → pause
            audioRef.current.pause();
            setPlayingRecordNumber(null);
            return;
        }
        // Stop any currently playing audio
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }
        // Resolve relative API paths against the backend base URL
        const resolvedUrl = audioUrl.startsWith("/")
            ? `${BASE_URL_DYNAMIC.replace(/\/$/, "")}${audioUrl}`
            : audioUrl;

        setLoadingAudioRecord(recordNumber);
        try {
            // Fetch with auth header so the [Authorize] endpoint accepts the request
            const token = getCookie("accessToken");
            const resp = await fetch(resolvedUrl, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const blob = await resp.blob();
            const blobUrl = URL.createObjectURL(blob);
            blobUrlRef.current = blobUrl;

            const audio = new Audio(blobUrl);
            audio.onended = () => setPlayingRecordNumber(null);
            audio.onerror = () => setPlayingRecordNumber(null);
            await audio.play();
            audioRef.current = audio;
            setPlayingRecordNumber(recordNumber);
        } catch {
            setPlayingRecordNumber(null);
        } finally {
            setLoadingAudioRecord(null);
        }
    }, [playingRecordNumber]);

    // Cleanup audio on unmount
    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
    }, []);

    // Create optimistic placeholder record for the new recording
    useEffect(() => {
        if (newRecordNumber && data === null) {
            const now = new Date();
            const optimisticRecord: TMeetingRecord = {
                recordNumber: newRecordNumber,
                userId: userId,
                therapistId: 0, // Will be populated when API returns real data
                date: now.toISOString(),
                time: now.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" }),
                type: t("MEETING_TYPE_THERAPY") || "טיפול",
                subject: "",
                status: MeetingRecordStatus.PROCESSING,
                processingStage: ProcessingStages.UPLOADING,
                duration: undefined,
                audioAnalyze: undefined,
                meetingSummary: "",
                optionsForReport: "",
                transcription: "",
                errorMessage: null,
            };
            // Show optimistic record immediately so UI doesn't feel blocked
            setData([optimisticRecord]);
        }
    }, [newRecordNumber, userId]);

    // Filter data based on search input
    const filtered = data?.filter(
        (r) =>
            r.type.includes(filter) ||
            r.date.includes(filter) ||
            String(r.userId).includes(filter) ||
            (r.subject && r.subject.includes(filter))
    );

    // Fetch records from API
    const fetchRecords = useCallback(() => {
        allRecords(Guid).then((response) => {
            if ("data" in response && response.data?.records && Array.isArray(response.data.records)) {
                const mappedData: TMeetingRecord[] = response.data.records.map((item) =>
                    mapMeetingRecordFromApi(item as Record<string, unknown>)
                );

                setData(mappedData);
            }
        }).finally(() => {
            setIsLoading(false);
        });
    }, [Guid, allRecords]);

    // Track if there are processing records for polling
    const hasProcessingRecords = data?.some(r => r.status === MeetingRecordStatus.PROCESSING) ?? false;

    // Initial fetch on mount
    useEffect(() => {
        fetchRecords();
    }, [fetchRecords]);

    // Separate effect for polling - only depends on hasProcessingRecords flag
    useEffect(() => {
        if (!hasProcessingRecords) return;
        
        const pollInterval = setInterval(() => {
            fetchRecords();
        }, 5000); // Poll every 5 seconds

        return () => clearInterval(pollInterval);
    }, [hasProcessingRecords, fetchRecords]);

    // Retry a failed record
    async function onRetry(recordNum: number) {
        setRetryingRecords(prev => new Set(prev).add(recordNum));
        try {
            const response = await retryRecord({ userGuid: Guid, recordNumber: recordNum }).unwrap();
            if (response.success) {
                // Update local state immediately
                setData(prev => prev?.map(r => 
                    r.recordNumber === recordNum 
                        ? { ...r, status: MeetingRecordStatus.PROCESSING, errorMessage: null }
                        : r
                ) || null);
                // Refresh data from server
                fetchRecords();
            }
        } catch {
            // Retry failed silently
        } finally {
            setRetryingRecords(prev => {
                const next = new Set(prev);
                next.delete(recordNum);
                return next;
            });
        }
    }

    function onDelete(recordNum: number) {
        setData(data?.filter((d) => d.recordNumber !== recordNum) || null);
        deleteRecord({ recordNum: recordNum, userId: userId });
    }

    function viewDetails(recordNum: number) {
        const r = data?.find(r => r.recordNumber === recordNum);
        if (r != null) {
            navigate(`/users/summaryMeeting/${recordNum}`, {
                state: {
                    Guid: Guid,
                    UsersId: userId,
                },
            });
        }
    }

    function navigateToRecording(appointmentId?: number) {
        navigate("/users/recording", {
            state: {
                Guid: Guid,
                UsersId: userId,
                patientName: userName,
                patientPhone: userPhone,
                patientBirth: userBirth,
                patientPassport: userPassport,
                ...(appointmentId ? { appointmentId } : {}),
            },
        });
    }

    const applyLinkUpdateLocally = useCallback((
        recordNumber: number,
        appointmentId: number | null,
        appointmentDate?: string | null,
        appointmentStartTime?: string | null,
        unassignedRecordNumbers: number[] = [],
        questionnaireCompaniesScoreUsersId?: number | null,
        cycle?: number | null
    ) => {
        setData(prev => prev?.map(record => {
            if (record.recordNumber === recordNumber) {
                return {
                    ...record,
                    appointmentId,
                    appointmentDate: appointmentId ? appointmentDate ?? record.appointmentDate : null,
                    appointmentStartTime: appointmentId ? appointmentStartTime ?? record.appointmentStartTime : null,
                    ...(questionnaireCompaniesScoreUsersId !== undefined
                        ? { questionnaireCompaniesScoreUsersId }
                        : {}),
                    ...(cycle !== undefined ? { cycle } : {}),
                };
            }
            if (unassignedRecordNumbers.includes(record.recordNumber)) {
                return {
                    ...record,
                    appointmentId: null,
                    appointmentDate: null,
                    appointmentStartTime: null,
                };
            }
            return record;
        }) || null);
    }, []);

    async function updateRecordAppointmentLink(record: TMeetingRecord, appointmentId: number | null, confirmReplace = false) {
        if (!Guid) return;
        setUpdatingLinkRecordNumber(record.recordNumber);
        try {
            const result = await updateAppointmentLink({
                userGuid: Guid,
                recordNumber: record.recordNumber,
                appointmentId,
                confirmReplace,
            }).unwrap();
            applyLinkUpdateLocally(
                record.recordNumber,
                result.appointmentId,
                result.appointmentDate,
                result.appointmentStartTime,
                result.unassignedRecordNumbers || [],
                result.questionnaireCompaniesScoreUsersId,
                result.cycle
            );
            fetchRecords();
        } catch (error: any) {
            if (error?.status === 409 && !confirmReplace) {
                const shouldReplace = window.confirm(t("RECORD_LINK_ALREADY_LINKED_WARNING"));
                if (shouldReplace) {
                    await updateRecordAppointmentLink(record, appointmentId, true);
                }
            }
        } finally {
            setUpdatingLinkRecordNumber(null);
        }
    }

    function openAppointmentAssignment(record: TMeetingRecord) {
        setRecordForAppointmentLink(record);
        setShowAppointmentPicker(true);
    }

    async function unassignAppointment(record: TMeetingRecord) {
        const confirmed = window.confirm(t("RECORD_LINK_UNASSIGN_CONFIRM"));
        if (!confirmed) return;
        await updateRecordAppointmentLink(record, null, true);
    }

    async function handleNewRecording() {
        // Don't navigate if we don't have valid user data
        if (!Guid || !userId) {
            navigate("/users");
            return;
        }

        // Check for pending recording sessions for this patient
        if (therapistId) {
            setCheckingPending(true);
            try {
                const result = await triggerGetPendingSessions(Number(therapistId)).unwrap();
                if (result?.success && result.sessions?.length > 0) {
                    const patientSessions = result.sessions.filter(
                        (s: any) => s.userId === userId
                    );
                    if (patientSessions.length > 0) {
                        setPendingSessions(patientSessions);
                        setShowPendingModal(true);
                        setCheckingPending(false);
                        return;
                    }
                }
            } catch {
                // If check fails, proceed to recording anyway
            }
            setCheckingPending(false);
        }

        setShowAppointmentPicker(true);
    }

    function handleBackClick() {
        // Navigate to users page instead of using navigate(-1) 
        // which can cause issues after page refresh
        navigate("/users");
    }

    const totalRecordings = data?.length || 0;
    const lang = i18n.language;
    return (
        <div className={styles.rtContainer} dir={i18n.dir()}>
            {/* Back Button */}
            <div className={styles.rtTopbar}>
                <BackArrowButton
                    text={t("ACTION_REPORT_BACK_BUTTON")}
                    onClick={handleBackClick}
                    position={"center"}
                />
            </div>

            {/* Patient Info Card */}
            <div className={styles.patientInfo}>
                <div className={styles.patientHeader}>
                    <div className={styles.patientAvatar}>
                        <span role="img" aria-label="user">👤</span>
                    </div>
                    <div className={styles.patientName}>
                        <h2>{userName}</h2>
                        <div className={styles.patientId}>
                            {t("PATIENT_FILE_NUMBER")}: PT-{userId || "---"}
                        </div>
                    </div>
                    <button
                        className={styles.recordBtn}
                        onClick={handleNewRecording}
                        disabled={checkingPending}
                        aria-label={t("NEW_RECORDING")}
                    >
                        <span className={styles.recordBtnIcon} role="img" aria-label="microphone">🎙️</span>
                        <span>{checkingPending ? "..." : t("NEW_RECORDING")}</span>
                    </button>
                </div>
                <div className={styles.patientDetails}>
                    <div className={styles.detailItem}>
                        <div className={styles.detailIcon}>📅</div>
                        <div className={styles.detailContent}>
                            <div className={styles.detailLabel}>{t("PATIENT_BIRTH_DATE")}</div>
                            <div className={styles.detailValue}>
                                {userBirth}
                            </div>
                        </div>
                    </div>
                    <div className={styles.detailItem}>
                        <div className={styles.detailIcon}>📞</div>
                        <div className={styles.detailContent}>
                            <div className={styles.detailLabel}>{t("PATIENT_PHONE")}</div>
                            <div className={styles.detailValue}>
                                {userPhone}
                            </div>
                        </div>
                    </div>
                    <div className={styles.detailItem}>
                        <div className={styles.detailIcon}>📧</div>
                        <div className={styles.detailContent}>
                            <div className={styles.detailLabel}>{t("PATIENT_EMAIL")}</div>
                            <div className={styles.detailValue}>
                                {userEmail}
                            </div>
                        </div>
                    </div>
                    <div className={styles.detailItem}>
                        <div className={styles.detailIcon}>🏥</div>
                        <div className={styles.detailContent}>
                            <div className={styles.detailLabel}>{t("RECORDINGS_STATUS")}</div>
                            <div className={styles.detailValue}>
                                {userStatusId === 1 
                                    ? t("STATUS_ACTIVE") 
                                    : t("STATUS_INACTIVE")}
                            </div>
                        </div>
                    </div>
                    <div className={styles.detailItem}>
                        <div className={styles.detailIcon}>📊</div>
                        <div className={styles.detailContent}>
                            <div className={styles.detailLabel}>{t("RECORDINGS_TOTAL_MEETINGS")}</div>
                            <div className={styles.detailValue}>
                                {totalRecordings}
                            </div>
                        </div>
                    </div>
                    <div className={styles.detailItem}>
                        <div className={styles.detailIcon}>🎙️</div>
                        <div className={styles.detailContent}>
                            <div className={styles.detailLabel}>{t("RECORDINGS_TOTAL")}</div>
                            <div className={styles.detailValue}>
                                {totalRecordings} {t("RECORDINGS_TOTAL")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recordings Section */}
            <div className={styles.recordingsSection}>
                <div className={styles.sectionHeader}>
                    <h2>📋 {t("RECORDINGS_LIST")}</h2>
                    <div className={styles.filterButtons}>
                        <button
                            className={`${styles.filterBtn} ${activeFilter === "all" ? styles.filterBtnActive : ""}`}
                            onClick={() => setActiveFilter("all")}
                        >
                            {t("FILTER_ALL")}
                        </button>
                        <button
                            className={`${styles.filterBtn} ${activeFilter === "month" ? styles.filterBtnActive : ""}`}
                            onClick={() => setActiveFilter("month")}
                        >
                            {t("FILTER_LAST_MONTH")}
                        </button>
                        <button
                            className={`${styles.filterBtn} ${activeFilter === "3months" ? styles.filterBtnActive : ""}`}
                            onClick={() => setActiveFilter("3months")}
                        >
                            {t("FILTER_3_MONTHS")}
                        </button>
                        <button
                            className={`${styles.filterBtn} ${activeFilter === "year" ? styles.filterBtnActive : ""}`}
                            onClick={() => setActiveFilter("year")}
                        >
                            {t("FILTER_YEAR")}
                        </button>
                    </div>
                </div>

                {/* Search Filter */}
                <div className={styles.Filter}>
                    <div className={styles.searchFilter}>
                        <span className={styles.searchIcon}>🔍</span>
                        <input
                            className={styles.searchInput}
                            type="text"
                            placeholder={t("FILTER")}
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        />
                    </div>
                </div>

                {/* Recordings Table */}
                <table className={styles.recordingsTable}>
                    <thead>
                        <tr>
                            <th>{t("RECORDINGS_DATE")}</th>
                            <th>{t("RECORDINGS_TIME")}</th>
                            <th>{t("RECORDINGS_DURATION")}</th>
	                            <th>{t("RECORDING_TYPE")}</th>
	                            <th>{t("RECORDINGS_SUBJECT")}</th>
	                            <th>{t("RECORDINGS_STATUS")}</th>
	                            <th>{t("RECORD_LINK_APPOINTMENT_STATUS")}</th>
	                            <th>{t("ACTIONS")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered && filtered.length > 0 ? (
                            filtered.map((r) => {
                                const isProcessing = r.status === MeetingRecordStatus.PROCESSING;
                                const isFailed = r.status === MeetingRecordStatus.FAILED;
                                const isRetrying = retryingRecords.has(r.recordNumber);
                                const currentStep = getCurrentStep(r.processingStage);
                                const totalSteps = 4;
                                const type = lang === "he" ? r.type : "Audio";

                                return (
                                    <tr
                                        key={r.recordNumber}
                                        onClick={() => !isFailed && viewDetails(r.recordNumber)}
                                        className={`${isProcessing ? styles.processingRow : ""} ${isFromToday(r.date) ? styles.rowFromToday : ""}`}
                                    >
                                        <td data-label={t("RECORDINGS_DATE")}>{formatDate(r.date)}</td>
                                        <td data-label={t("RECORDINGS_TIME")}>
                                            {formatTime(r.date, r.time) || "—"}
                                        </td>
                                        <td data-label={t("RECORDINGS_DURATION")}>
                                            {r.duration ? (
                                                <span className={styles.durationBadge}>
                                                    {formatDuration(r.duration)}
                                                </span>
                                            ) : (
                                                <span>—</span>
                                            )}
                                        </td>
                                        <td data-label={t("RECORDING_TYPE")}>{type}</td>
                                        <td data-label={t("RECORDINGS_SUBJECT")} className={styles.subjectCell} title={r.subject || ""}>
                                            {r.subject || "—"}
                                        </td>
                                        <td data-label={t("RECORDINGS_STATUS")}>
                                            {isProcessing ? (
                                                <div className={styles.processingStatus}>
                                                    <div className={styles.stepper}>
                                                        {[1, 2, 3, 4, 5, 6].map((step) => (
                                                            <div
                                                                key={step}
                                                                className={`${styles.stepDot} ${
                                                                    step <= currentStep ? styles.stepDotActive : ''
                                                                } ${step === currentStep ? styles.stepDotCurrent : ''}`}
                                                                title={step <= currentStep ? getStageLabel(r.processingStage) : ''}
                                                            />
                                                        ))}
                                                    </div>
                                                    <span className={styles.processingText}>
                                                        {getStageLabel(r.processingStage)} ({currentStep}/{totalSteps})
                                                    </span>
                                                </div>
                                            ) : isFailed ? (
                                                <div className={styles.failedStatus}>
                                                    <span className={`${styles.statusBadge} ${styles.statusFailed}`}>
                                                        {t("STATUS_FAILED")}
                                                    </span>
                                                    {r.errorMessage && (
                                                        <span className={styles.errorTooltip} title={r.errorMessage}>
                                                            ⚠️
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className={`${styles.statusBadge} ${getStatusBadgeClass(r.status)}`}>
                                                    {getStatusText(r.status)}
                                                </span>
	                                            )}
	                                        </td>
	                                        <td data-label={t("RECORD_LINK_APPOINTMENT_STATUS")}>
	                                            {r.appointmentId ? (
	                                                <span className={styles.linkedAppointmentBadge}>
	                                                    {t("LINKED_TO_APPOINTMENT")} {r.appointmentDate ? formatDate(r.appointmentDate) : ""}
	                                                    {r.appointmentStartTime ? `, ${r.appointmentStartTime}` : ""}
	                                                </span>
	                                            ) : (
	                                                <span className={styles.unlinkedAppointmentBadge}>{t("NOT_LINKED")}</span>
	                                            )}
	                                        </td>
	                                        <td className={styles.actionsCell}>
                                            {isProcessing ? (
                                                <button
                                                    className={styles.viewBtn}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        viewDetails(r.recordNumber);
                                                    }}
                                                >
                                                    {t("VIEW_PROGRESS")}
                                                </button>
                                            ) : isFailed ? (
                                                <button
                                                    className={styles.retryBtn}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onRetry(r.recordNumber);
                                                    }}
                                                    disabled={isRetrying}
                                                >
                                                    {isRetrying ? t("RETRYING") : t("RETRY")}
                                                </button>
                                            ) : (
                                                <>
                                                    <button
                                                        className={styles.viewBtn}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            viewDetails(r.recordNumber);
                                                        }}
                                                    >
                                                        {t("VIEW_DETAILS")}
                                                    </button>
                                                    {r.audioFileUrl ? (
                                                        <button
                                                            className={styles.playBtn}
                                                            disabled={loadingAudioRecord === r.recordNumber}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleAudioPlayback(r.recordNumber, r.audioFileUrl!);
                                                            }}
                                                            aria-label={playingRecordNumber === r.recordNumber ? t("PENDING_RECORDING_PAUSE") : t("PENDING_RECORDING_PLAY")}
                                                            title={playingRecordNumber === r.recordNumber ? t("PENDING_RECORDING_PAUSE") : t("PENDING_RECORDING_PLAY")}
                                                        >
                                                            {loadingAudioRecord === r.recordNumber ? "⏳" : playingRecordNumber === r.recordNumber ? "⏸" : "▶"}
                                                        </button>
                                                    ) : r.status === "complete" ? (
                                                        <button
                                                            className={`${styles.playBtn} ${styles.playBtnDisabled}`}
                                                            disabled
                                                            aria-label={t("RECORDING_DELETED_BY_POLICY")}
                                                            title={t("RECORDING_DELETED_BY_POLICY")}
                                                        >
                                                            ▶
                                                        </button>
                                                    ) : null}
                                                    <button
                                                        className={styles.deleteBtn}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onDelete(r.recordNumber);
                                                        }}
                                                        aria-label={t("DELETE")}
                                                    >
                                                        {t("DELETE")}
                                                    </button>
	                                                </>
	                                            )}
	                                            <button
	                                                className={styles.linkBtn}
	                                                onClick={(e) => {
	                                                    e.stopPropagation();
	                                                    openAppointmentAssignment(r);
	                                                }}
	                                                disabled={updatingLinkRecordNumber === r.recordNumber}
	                                            >
	                                                {r.appointmentId ? t("RECORD_LINK_CHANGE_APPOINTMENT") : t("RECORD_LINK_ASSIGN_APPOINTMENT")}
	                                            </button>
	                                            {r.appointmentId && (
	                                                <button
	                                                    className={styles.unlinkBtn}
	                                                    onClick={(e) => {
	                                                        e.stopPropagation();
	                                                        unassignAppointment(r);
	                                                    }}
	                                                    disabled={updatingLinkRecordNumber === r.recordNumber}
	                                                >
	                                                    {t("RECORD_LINK_UNASSIGN_APPOINTMENT")}
	                                                </button>
	                                            )}
	                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
	                                <td colSpan={8} style={{ textAlign: "center", padding: "40px" }}>
                                    {isLoading && !data?.length ? (
                                        <div className={styles.loadingContainer}>
                                            <div className={styles.loadingSpinner}></div>
                                            <span>{t("LOADING")}</span>
                                        </div>
                                    ) : t("NO_RECORDINGS_FOUND")}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Appointment Picker Modal */}
	            {showAppointmentPicker && (
	                <Modal onClose={() => {
	                    setShowAppointmentPicker(false);
	                    if (recordForAppointmentLink) {
	                        setRecordForAppointmentLink(null);
	                    } else {
	                        navigateToRecording();
	                    }
	                }}>
	                    <AppointmentPicker
	                        patientId={Number(userId)}
	                        patientName={userName || ""}
	                        titleKey={recordForAppointmentLink ? "RECORD_LINK_SELECT_APPOINTMENT_TITLE" : "APPT_PICKER_TITLE"}
	                        subtitleKey={recordForAppointmentLink ? "RECORD_LINK_SELECT_APPOINTMENT_SUBTITLE" : "APPT_PICKER_SUBTITLE"}
	                        confirmLabelKey={recordForAppointmentLink ? "RECORD_LINK_ASSIGN_APPOINTMENT" : "APPT_PICKER_LINK_AND_CONTINUE"}
	                        skipLabelKey={recordForAppointmentLink ? "CANCEL" : "APPT_PICKER_SKIP"}
	                        onSelect={async (appointmentId) => {
	                            setShowAppointmentPicker(false);
	                            if (recordForAppointmentLink) {
	                                const record = recordForAppointmentLink;
	                                setRecordForAppointmentLink(null);
	                                await updateRecordAppointmentLink(record, appointmentId);
	                            } else {
	                                navigateToRecording(appointmentId);
	                            }
	                        }}
	                        onSkip={() => {
	                            setShowAppointmentPicker(false);
	                            if (recordForAppointmentLink) {
	                                setRecordForAppointmentLink(null);
	                            } else {
	                                navigateToRecording();
	                            }
	                        }}
	                    />
                </Modal>
            )}

            {/* Pending Recording Recovery Modal */}
            {showPendingModal && pendingSessions.length > 0 && (
                <Modal onClose={() => setShowPendingModal(false)}>
                    <PendingRecordingPopup
                        sessions={pendingSessions}
                        onClose={() => setShowPendingModal(false)}
                        onDismissed={(sessionId) => {
                            const remaining = pendingSessions.filter((s: any) => s.sessionId !== sessionId);
                            setPendingSessions(remaining);
                            if (remaining.length === 0) {
                                setShowPendingModal(false);
                                navigateToRecording();
                            }
                        }}
                        onFinalized={(sessionId, fUserId, recordNumber, patientGuid, patientName) => {
                            setPendingSessions([]);
                            setShowPendingModal(false);
                            // Refresh the table to show the newly finalized record
                            if (userId) allRecords(userId);
                        }}
                        onContinueRecording={(session) => {
                            setShowPendingModal(false);
                            navigate("/users/recording", {
                                state: {
                                    Guid: session.patientGuid,
                                    UsersId: session.userId,
                                    patientName: session.patientName,
                                    appointmentId: session.appointmentId ?? undefined,
                                    appointmentStartTime: session.appointmentStartTime ?? undefined,
                                    resumeSession: {
                                        sessionId: session.sessionId,
                                        chunkCount: session.chunkCount,
                                        mimeType: session.mimeType,
                                        durationSeconds: session.recordingDurationSeconds || 0,
                                    },
                                },
                            });
                        }}
                    />
                </Modal>
            )}
        </div>
    );
}
